<script setup>
import {ref, onMounted, nextTick} from 'vue'
import {
  PhPen,
  PhPlus, PhTrash
} from "@phosphor-icons/vue";
import {fetchDevices, upsertDevice, deleteDevice} from "~/lib/api/devices";
import {useRoute} from "vue-router";

// get org id from vue router with params organization_uuid
const route = useRoute();
const props = defineProps({
  machineId: {type: String}
})

const organizationUuid = route.params.organization_uuid;
const machineId = route.params.machine_id || props.machineId;

// Reactive state elements
const devices = ref([])
const scanning = ref(false);
const activeDevice = ref(null)
const isDrawerOpen = ref(false)
const isModalOpen = ref(false)
const isEditing = ref(false)

// Form blueprint template (mapping Vue components to Rails parameters)
const formDefault = {id: null, name: '', serial_number: '', sensor_type: 'Temperature', status: 'Online'}
const form = ref({...formDefault})

// Fetch initial device array data from Rails index action
const _fetchDevices = async () => {
  try {
    const res = await fetchDevices(organizationUuid, machineId);
    devices.value = await res.data;
  } catch (err) {
    console.error('Failed fetching payload array:', err)
  }
}

// Load lifecycle initial hook
onMounted(_fetchDevices)

// Open Drawer Sidebar layout details
const openSidebar = (device) => {
  activeDevice.value = device
  isDrawerOpen.value = true
}

// Form visibility handler
const openFormModal = (device = null) => {
  if (device) {
    isEditing.value = true
    form.value = {...device}
  } else {
    isEditing.value = false
    form.value = {...formDefault}
  }
  isModalOpen.value = true
}

// POST/PATCH Form Submit router processing to Rails API endpoints
const handleSubmit = async () => {
  const method = isEditing.value ? 'PUT' : 'POST'

  try {
    const res = await upsertDevice(organizationUuid, form.value);

    if (res.ok) {
      const savedDevice = await res.json()
      await _fetchDevices() // Reload clean active UI payload layout matrix state

      // Keep active tracking details block updated
      if (activeDevice.value?.id === savedDevice.id) {
        activeDevice.value = savedDevice
      }
      isModalOpen.value = false
    }
  } catch (err) {
    console.error('Could not commit state update:', err)
  }
}

// DELETE Request handling interface
const _deleteDevice = async (id) => {
  try {
    const res = await fetch(`${organizationUuid}/devices/${id}`, {method: 'DELETE'})
    if (res.ok) {
      if (activeDevice.value?.id === id) isDrawerOpen.value = false
      await fetchDevices()
    }
  } catch (err) {
    console.error('Could not commit state update:', err)
  }
}


const _openScanner = async () => {
  console.log("Opening QR Scanner")
}
</script>

<template>
  <div v-if="scanning" id="qr-reader" class="qr-viewport"></div>

  <div class="drawer drawer-end min-h-screen ">
    <input id="status-sidebar" type="checkbox" class="drawer-toggle" :checked="isDrawerOpen"
           @change="isDrawerOpen = $event.target.checked"/>

    <!-- MAIN INTERFACE PAGE -->
    <div class="drawer-content p-6 flex flex-col gap-6">

      <!-- Top Action Bar -->
      <div class="flex justify-between  items-center dark:border-gray-700 border p-4 rounded-xl shadow-sm">
        <div>
          <h1 class="text-2xl font-bold">Devices</h1>
          <p class="text-sm opacity-60">Devices able to push telemetry</p>
        </div>
        <a :href="`/dashboard/organizations/${organizationUuid}/devices/new`">
          <button class="btn btn-sm gap-2">
            <PhPlus size="18" weight="bold"/>
            <span>Add Device</span>
          </button>
        </a>
        <!--        <button @click="openFormModal()" class="btn btn-sm gap-2">-->
        <!--          <PhPlus size="18" weight="bold"/>-->
        <!--          Add Device-->
        <!--        </button>-->
      </div>

      <!-- Sensor Card List Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
            v-for="device in devices"
            :key="device.id"
            @click="openSidebar(device)"
            class="card bg-base-100 shadow-xl hover:shadow-2xl transition-all cursor-pointer border border-transparent hover:border-primary group"
        >
          <div class="card-body">
            <div class="flex justify-between items-start">
              <div>
                <span class="badge badge-sm font-medium mb-1 badge-secondary">
                  {{ device.device_type }}
                </span>
                <h2 class="card-title text-lg group-hover:text-primary transition-colors">{{ device.name }}</h2>
              </div>
              <span class="badge font-semibold"
                    :class="device.status === 'Online' ? 'badge-success text-success-content' : 'badge-ghost'">
                {{ device.status }}
              </span>
            </div>

            <!-- Card Actions -->
            <div class="card-actions justify-end mt-4 pt-2 border-t border-base-200" @click.stop>
              <a :href="`/dashboard/organizations/${organizationUuid}/devices/${device.id}/edit`"
                 class="btn btn-sm btn-ghost">
                <PhPen size="12" weight="bold"/>
                <span>Edit</span>
              </a>
              <button @click="deleteDevice(device.id)" class="btn btn-sm btn-ghost text-error">
                <PhTrash size="12" weight="bold"/>
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SIDEBAR DRAWER OVERLAY & PANEL -->
    <div class="drawer-side z-40">
      <label for="status-sidebar" aria-label="close sidebar" class="drawer-overlay"></label>
      <div class="menu p-6 w-80 min-h-full bg-base-100 text-base-content flex flex-col justify-between">

        <div v-if="activeDevice">
          <div class="flex justify-between items-center mb-6 border-b pb-4">
            <h3 class="text-xl font-bold">Device Live Status</h3>
            <button @click="isDrawerOpen = false" class="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <div class="stats stats-vertical shadow w-full bg-base-200 gap-1 mb-4">
            <div class="stat">
              <div class="stat-title">Device Name</div>
              <div class="text-lg font-bold break-words">{{ activeDevice.name }}</div>
            </div>

            <div class="stat">
              <div class="stat-title">Current Metric Value</div>
              <div class="stat-value text-primary">{{ activeDevice.reading }}</div>
            </div>

            <div class="stat">
              <div class="stat-title">Network State</div>
              <div class="stat-value text-sm flex items-center gap-2 mt-1">
                <span class="badge" :class="activeDevice.status === 'Online' ? 'badge-success' : 'badge-error'"></span>
                {{ activeDevice.status }}
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center opacity-50 py-10">No device active.</div>
      </div>
    </div>
  </div>

  <!-- CREATE / EDIT MODAL LAYER -->
  <dialog class="modal" :class="{ 'modal-open': isModalOpen }">
    <div class="modal-box">
      <h3 class="font-bold text-lg mb-4">{{ isEditing ? 'Edit Hardware Config' : 'Register New Device' }}</h3>

      <form @submit.prevent="handleSubmit" class="flex flex-col gap-4">
        <fieldset class="fieldset">
          <legend class="fieldset-legend">Device Name</legend>
          <input v-model="form.name" type="text" placeholder="e.g. Zone B Thermostat"
                 class="input input-bordered w-full" required/>
        </fieldset>
        <fieldset class="fieldset">
          <legend class="fieldset-legend">Device Serial number</legend>
          <!--    <button class="btn btn-sm btn-primary w-full" @click.prevent="_openScanner()">Scan QR / Bar Code</button> -->
          <input v-model="form.serial_number" type="text" placeholder="e.g. 1234567890"
                 class="input input-bordered w-full" required/>
        </fieldset>

        <fieldset class="fieldset">
          <legend class="fieldset-legend">Sensor Classification</legend>
          <select v-model="form.sensor_type" class="select select-bordered w-full">
            <option>Temperature</option>
            <option>Humidity</option>
            <option>Pressure</option>
          </select>
        </fieldset>

        <fieldset class="fieldset">
          <legend class="fieldset-legend">Deployment Status</legend>
          <div class="flex gap-4 mt-1">
            <label class="label cursor-pointer gap-2">
              <input type="radio" v-model="form.status" value="Online" class="radio"/>
              <span class="label-text">Online</span>
            </label>
            <label class="label cursor-pointer gap-2">
              <input type="radio" v-model="form.status" value="Offline" class="radio"/>
              <span class="label-text">Offline</span>
            </label>
          </div>
        </fieldset>

        <div class="modal-action">
          <button type="button" @click="isModalOpen = false" class="btn btn-ghost">Cancel</button>
          <button type="submit" class="btn btn-primary">
            {{ isEditing ? 'Update Device' : 'Register Device' }}
          </button>
        </div>
      </form>
    </div>
    <div class="modal-backdrop dark:bg-black/60 dark:backdrop-blur-sm" @click="isModalOpen = false"></div>
  </dialog>
</template>
